import { Injectable, inject } from '@angular/core';
import { Permiso, SesionService } from './sesion.service';

/** Los roles del sistema. Los permisos finos se definen en el backend. */
export type Rol =
  | 'gestor_proyecto'
  | 'mercado'
  | 'responsable_mercado'
  | 'direccion_general'
  | 'control_gestion'
  | 'administrador'
  | 'administrador_usuarios';

export interface MenuItem {
  name: string;
  icon: string;
  route: string;
  /** Quienes ven la seccion. Se conserva como documentacion de la matriz. */
  roles?: Rol[];
  /**
   * El permiso que hace falta para verla.
   *
   * Es lo que se aplica de verdad: el backend devuelve permisos, no roles, y
   * asi agregar un rol que ve el monitor no obliga a tocar el menu.
   */
  permiso?: Permiso;

  /**
   * Las secciones que cuelgan de esta.
   *
   * Se muestran debajo, indentadas. Heredan el permiso del padre: si no se ve
   * la sección, tampoco sus partes.
   */
  hijos?: Array<{ name: string; route: string; detalle?: string }>;
}

@Injectable({
  providedIn: 'root'
})
export class MenuService {
  private menuList: MenuItem[] = [
    {
      name: 'Dashboard',
      icon: 'logos/element-2.svg',
      route: '/dashboard',
      permiso: 'ver_dashboard',
      // Administracion y Administrador de Usuarios no lo ven
      roles: [
        'gestor_proyecto',
        'mercado',
        'responsable_mercado',
        'direccion_general',
        'control_gestion'
      ]
    },
    {
      /* **El reporte de salud** (3/11/2026). Lo ven los mismos que el
         dashboard, cada uno con su alcance: el backend acota. */
      name: 'Salud del portafolio',
      icon: 'logos/chart-square.svg',
      route: '/reportes/salud',
      // Mientras se valida, solo la lista piloto de Configuración
      permiso: 'ver_reporte_salud',
      roles: [
        'gestor_proyecto',
        'mercado',
        'responsable_mercado',
        'direccion_general',
        'control_gestion'
      ]
    },
    {
      /* Es de operacion, no de gestion de proyectos. Los dos que lo ven pueden
         ademas accionar reprocesos. */
      name: 'Sincronización',
      icon: 'logos/component.svg',
      route: '/monitor',
      permiso: 'ver_monitor',
      roles: ['administrador', 'control_gestion'],
      /* Las dos colas, cada una con su ruta. Son circuitos distintos —distinta
         causa cuando fallan y distinta acción para arreglarlos— así que se
         llega directo a la que interesa en vez de entrar y buscar. */
      hijos: [
        { name: 'Lo que importamos', route: '/monitor', detalle: 'de ARMUS' },
        { name: 'Lo que enviamos', route: '/monitor/envios', detalle: 'hacia ARMUS' }
      ]
    },
    {
      /* **El disparador del cierre mensual**, y por eso está al lado de
         Sincronización y no adentro: el monitor cuenta lo que ya pasó, esto
         hace que pase. Lo ve Control de Gestión, que es quien decide que un
         mes está completo. */
      name: 'Cierre de período',
      icon: 'logos/forward-square.svg',
      route: '/cierre',
      permiso: 'cerrar_periodo',
      roles: ['administrador', 'control_gestion']
    },
    {
      /* Roles y alcances. Solo el Administrador de Usuarios. */
      name: 'Usuarios',
      icon: 'logos/profile-2user.svg',
      route: '/usuarios',
      permiso: 'administrar_usuarios',
      roles: ['administrador_usuarios']
    },
    {
      /* **La configuración deja de ser una API.** El mapeo de empresas, el
         modo de acceso y los catálogos vivían en el backend sin ninguna
         pantalla: para tocarlos había que saber el nombre del parámetro y
         armar el request a mano. */
      name: 'Configuración',
      icon: 'logos/setting-2.svg',
      route: '/configuracion',
      permiso: 'ver_configuracion'
    }
  ];

  private sesion = inject(SesionService);

  getMenuList(): MenuItem[] {
    return this.menuList;
  }

  /** Lo que ve quien esta adentro. Es lo que usa el menu lateral. */
  getMenuVisible(): MenuItem[] {
    return this.menuList.filter((i) => !i.permiso || this.sesion.puede(i.permiso));
  }

  /** Lo que ve un rol. Sirve para documentar y probar la matriz. */
  getMenuPara(rol: Rol): MenuItem[] {
    return this.menuList.filter((i) => !i.roles || i.roles.includes(rol));
  }

  setMenuList(menu: MenuItem[]): void {
    this.menuList = menu;
  }
}
